import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import MovieSection from '@/pages/home/components/MovieSection';
import { movies, categories } from '@/mocks/movies';
import type { Movie } from '@/mocks/movies';

const categoryNameMap: Record<string, string> = {
  movie: '电影',
  tv: '电视剧',
  anime: '动漫',
  variety: '综艺',
  documentary: '纪录片',
  kids: '少儿',
};

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeSlug, setActiveSlug] = useState(slug || 'movie');
  const [filteredMovies, setFilteredMovies] = useState<Movie[]>([]);

  useEffect(() => {
    if (slug) {
      setActiveSlug(slug);
    }
  }, [slug]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [activeSlug]);

  useEffect(() => {
    setFilteredMovies(movies.filter((m) => m.category === activeSlug));
  }, [activeSlug]);

  const categoryName = categoryNameMap[activeSlug] || '电影';
  const pageTitle = `悟空影视${categoryName} - 悟空影视免费${categoryName}在线观看`;

  const webpageSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: pageTitle,
    description: `悟空影视免费${categoryName}在线观看，海量高清${categoryName}资源，无需注册即点即播，流畅不缓冲。`,
    url: `https://www.wukongyingshi.com/category/${activeSlug}`,
    isPartOf: {
      '@type': 'WebSite',
      name: '悟空影视',
      url: 'https://www.wukongyingshi.com',
    },
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(webpageSchema)}</script>
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20">
          <div className="w-full px-4 md:px-6 py-6">
            <div className="max-w-7xl mx-auto">
              <Breadcrumb
                items={[
                  { label: '首页', href: '/' },
                  { label: categoryName },
                ]}
              />
            </div>
          </div>

          <section className="w-full px-4 md:px-6 py-4">
            <div className="max-w-7xl mx-auto">
              <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-2">
                悟空影视{categoryName}
              </h1>
              <p className="text-sm text-foreground-500 mb-6">
                海量高清{categoryName}资源，免费在线观看，无需注册，即点即播
              </p>

              <div className="flex items-center gap-2 py-1 mb-8 overflow-x-auto">
                {categories.map((cat) => (
                  <Link
                    key={cat.id}
                    to={`/category/${cat.slug}`}
                    className={`px-5 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all duration-200 cursor-pointer ${
                      activeSlug === cat.slug
                        ? 'bg-primary-500 text-white'
                        : 'bg-background-100 text-foreground-600 hover:bg-background-200 border border-background-200'
                    }`}
                  >
                    {cat.name}
                  </Link>
                ))}
              </div>

              {filteredMovies.length > 0 ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-5">
                  {filteredMovies.map((movie) => (
                    <Link
                      key={movie.id}
                      to={`/article/${movie.id}`}
                      className="group cursor-pointer"
                    >
                      <div className="relative aspect-[3/4] rounded-lg overflow-hidden bg-background-200 mb-3">
                        <img
                          src={movie.imageUrl}
                          alt={`${movie.title} - 悟空影视免费在线观看`}
                          title={`${movie.title} ${movie.year} - ${movie.tags.join(' ')}`}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                          loading="lazy"
                        />
                        <div className="absolute top-2 left-2">
                          <span className="px-2 py-0.5 rounded text-[10px] font-medium bg-accent-500 text-white">
                            {movie.rating}
                          </span>
                        </div>
                        {movie.updateInfo && (
                          <div className="absolute top-2 right-2">
                            <span className="px-2 py-0.5 rounded text-[10px] font-medium bg-black/60 text-white backdrop-blur-sm">
                              {movie.updateInfo}
                            </span>
                          </div>
                        )}
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                          <div className="w-10 h-10 flex items-center justify-center rounded-full bg-white/80 opacity-0 group-hover:opacity-100 transition-opacity">
                            <i className="ri-play-fill text-lg text-primary-600"></i>
                          </div>
                        </div>
                      </div>
                      <h3 className="text-sm font-medium text-foreground-800 group-hover:text-primary-600 transition-colors line-clamp-1">
                        {movie.title}
                      </h3>
                      <p className="text-xs text-foreground-500 mt-1">
                        {movie.year} · {movie.region}
                      </p>
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="text-center py-20">
                  <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100 mb-4">
                    <i className="ri-movie-line text-2xl text-foreground-400"></i>
                  </div>
                  <p className="text-foreground-500">该分类暂无内容，敬请期待</p>
                </div>
              )}
            </div>
          </section>
        </main>
        <Footer />
      </div>
    </>
  );
}